import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, MoveVertical, Play, Pointer, RotateCcw, Ruler, Square, X } from 'lucide-react';

// Tutorial animado de "Medir" (~20 s, salteable): una obra dibujada con la misma perspectiva que una cámara de
// obra; una manito toca las 4 esquinas de la platea, se escribe cuánto mide, aparecen las baldosas de 1 m
// ("cámara calibrada") y después mide una distancia de 12,4 m. Todo SVG + React, sin videos ni librerías.

const VW = 800;
const VH = 450;
const HOR = 150; // horizonte
const FOC = 520;
const ALTO = 8;
const P = (X, Z) => [VW / 2 + (X * FOC) / Z, HOR + (ALTO * FOC) / Z];
const DURACION = 20;

// platea de 10 × 6 m
const ESQ = [P(-7, 17), P(3, 17), P(3, 23), P(-7, 23)];
const TAPS_ESQ = [1.65, 2.75, 3.85, 4.95];
// distancia medida: 12,4 m
const MA = P(-9, 30);
const MB = P(3.2, 32.3);
const TAPS_MED = [14.5, 16.0];

const MANO = [
  [0, 760, 480], [1.4, ...ESQ[0]], [1.9, ...ESQ[0]], [2.5, ...ESQ[1]], [3.0, ...ESQ[1]], [3.6, ...ESQ[2]], [4.1, ...ESQ[2]],
  [4.7, ...ESQ[3]], [5.2, ...ESQ[3]], [6.1, 840, 520], [13.4, 840, 520], [14.3, ...MA], [14.7, ...MA], [15.6, ...MB], [16.2, ...MB], [17.3, 840, 520],
];
const suave = (x) => x * x * (3 - 2 * x);
const posMano = (t) => {
  for (let i = 0; i < MANO.length - 1; i++) {
    const [t0, x0, y0] = MANO[i];
    const [t1, x1, y1] = MANO[i + 1];
    if (t <= t1) {
      const u = suave(Math.min(1, Math.max(0, (t - t0) / (t1 - t0))));
      return [x0 + (x1 - x0) * u, y0 + (y1 - y0) * u];
    }
  }
  const u = MANO[MANO.length - 1];
  return [u[1], u[2]];
};
const entre = (t, a, b) => Math.min(1, Math.max(0, (t - a) / (b - a)));

const ETAPAS = [
  { hasta: 1.4, texto: 'Primero calibrás la cámara. Una sola vez.' },
  { hasta: 5.6, texto: 'Tocá las 4 esquinas de algo del piso' },
  { hasta: 10, texto: 'Escribí cuánto mide' },
  { hasta: 13.2, texto: '¡Listo! La cámara quedó calibrada' },
  { hasta: 17, texto: 'Ahora medí: tocá dos puntos' },
  { hasta: DURACION, texto: 'Y ya tenés la medida' },
];

function Escena({ t }) {
  const nEsq = TAPS_ESQ.filter((x) => t >= x).length;
  const relleno = entre(t, 5.1, 5.6);
  const grilla = entre(t, 10, 10.6) * (1 - entre(t, 13, 13.5));
  const calibrada = t >= 10.2;
  const nMed = TAPS_MED.filter((x) => t >= x).length;
  const largo = entre(t, 16.0, 16.5);
  const cartel = entre(t, 16.6, 16.9);
  const ladosConMedida = t >= 9.4;
  const rippleDe = (taps) => taps.map((x) => (t >= x && t < x + 0.6 ? (t - x) / 0.6 : null));
  const pathEsq = ESQ.slice(0, nEsq).map((p, i) => `${i ? 'L' : 'M'}${p[0]},${p[1]}`).join(' ');
  const baldosas = [];
  if (grilla > 0) {
    for (let x = -11; x < 7; x++) {
      for (let z = 15; z < 32; z++) {
        const q = [P(x, z), P(x + 1, z), P(x + 1, z + 1), P(x, z + 1)];
        baldosas.push(<path key={`${x}_${z}`} d={`M${q.map((p) => p.join(',')).join(' L')} Z`} fill={(x + z) % 2 === 0 ? '#ffffff' : '#2563eb'} fillOpacity={0.22} stroke="#ffffff" strokeOpacity={0.8} strokeWidth={0.8} />);
      }
    }
  }
  const sombra = { filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.45))' };
  const mid = (a, b) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  const pA = MA;
  const pB = [MA[0] + (MB[0] - MA[0]) * largo, MA[1] + (MB[1] - MA[1]) * largo];
  const mAB = mid(MA, MB);
  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="absolute inset-0 w-full h-full" aria-hidden="true">
      <defs>
        <linearGradient id="mt-cielo" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#bfdbfe" /><stop offset="1" stopColor="#eff6ff" /></linearGradient>
        <linearGradient id="mt-piso" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#c8b79b" /><stop offset="1" stopColor="#9c7f5c" /></linearGradient>
      </defs>
      <rect width={VW} height={HOR} fill="url(#mt-cielo)" />
      {/* ciudad de fondo */}
      <g fill="#94a3b8">
        <rect x="30" y="70" width="70" height="80" /><rect x="110" y="95" width="50" height="55" /><rect x="560" y="60" width="60" height="90" />
        <rect x="630" y="88" width="80" height="62" /><rect x="720" y="105" width="60" height="45" />
      </g>
      <g fill="#cbd5e1">{[0, 1, 2, 3, 4, 5].map((i) => <rect key={i} x={572 + (i % 2) * 22} y={72 + Math.floor(i / 2) * 22} width="12" height="12" />)}</g>
      {/* grúa */}
      <g stroke="#f59e0b" strokeWidth="4" fill="none">
        <line x1="640" y1="150" x2="640" y2="30" /><line x1="560" y1="34" x2="760" y2="34" /><line x1="700" y1="34" x2="700" y2="80" strokeWidth="1.5" />
      </g>
      <rect y={HOR} width={VW} height={VH - HOR} fill="url(#mt-piso)" />
      {/* huellas y charco */}
      <path d={`M ${P(-14, 40).join(',')} Q 420 260 ${P(12, 14).join(',')}`} stroke="#8b6b47" strokeWidth="5" fill="none" opacity="0.35" />
      <ellipse cx="640" cy="390" rx="70" ry="14" fill="#93c5fd" opacity="0.5" />
      {/* platea */}
      <path d={`M${ESQ.map((p) => p.join(',')).join(' L')} Z`} fill="#d6d3d1" stroke="#a8a29e" strokeWidth="2" />
      {/* contenedor */}
      {(() => {
        const a = P(6, 24); const b = P(12, 24); const h = (2.6 * FOC) / 24;
        const c = P(12, 26.4); const hc = (2.6 * FOC) / 26.4;
        return (
          <g>
            <path d={`M${b[0]},${b[1]} L${c[0]},${c[1]} L${c[0]},${c[1] - hc} L${b[0]},${b[1] - h} Z`} fill="#c2410c" />
            <rect x={a[0]} y={a[1] - h} width={b[0] - a[0]} height={h} fill="#ea580c" />
            {[1, 2, 3, 4, 5, 6, 7].map((i) => <line key={i} x1={a[0] + ((b[0] - a[0]) * i) / 8} y1={a[1] - h + 3} x2={a[0] + ((b[0] - a[0]) * i) / 8} y2={a[1] - 3} stroke="#c2410c" strokeWidth="2" />)}
          </g>
        );
      })()}
      {/* baño químico */}
      {(() => { const b = P(-12, 26); const h = (2.3 * FOC) / 26; return <g><rect x={b[0] - 12} y={b[1] - h} width="24" height={h} fill="#2563eb" rx="2" /><rect x={b[0] - 12} y={b[1] - h} width="24" height="6" fill="#1d4ed8" /></g>; })()}

      {grilla > 0 && <g opacity={grilla}>{baldosas}</g>}

      {/* rectángulo marcado */}
      {nEsq >= 2 && <path d={pathEsq} fill="none" stroke="#2563eb" strokeWidth="4" strokeLinejoin="round" style={sombra} />}
      {relleno > 0 && <path d={`M${ESQ.map((p) => p.join(',')).join(' L')} Z`} fill="#2563eb" fillOpacity={0.28 * relleno} stroke="#2563eb" strokeWidth="4" strokeOpacity={relleno} strokeLinejoin="round" />}
      {ESQ.slice(0, nEsq).map((p, i) => (
        <g key={i} style={sombra}>
          <circle cx={p[0]} cy={p[1]} r="10" fill="#fff" stroke="#2563eb" strokeWidth="3.5" />
          <text x={p[0]} y={p[1] + 4.5} textAnchor="middle" fontSize="12" fontWeight="800" fill="#1d4ed8">{i + 1}</text>
        </g>
      ))}
      {/* cuando se cierra el rectángulo, se pintan los dos lados que hay que medir: largo rojo, ancho azul (igual que en la calibración real) */}
      {relleno > 0 && (
        <g strokeLinecap="round" style={sombra} opacity={relleno}>
          <line x1={ESQ[0][0]} y1={ESQ[0][1]} x2={ESQ[1][0]} y2={ESQ[1][1]} stroke="#dc2626" strokeWidth="7" />
          <line x1={ESQ[1][0]} y1={ESQ[1][1]} x2={ESQ[2][0]} y2={ESQ[2][1]} stroke="#2563eb" strokeWidth="7" />
        </g>
      )}
      {relleno > 0 && (
        <g fontSize="17" fontWeight="800" textAnchor="middle" stroke="#fff" strokeWidth="5" paintOrder="stroke" opacity={relleno}>
          <text x={mid(ESQ[0], ESQ[1])[0]} y={mid(ESQ[0], ESQ[1])[1] + 26} fill="#dc2626">{ladosConMedida ? 'Largo: 10 m' : 'Largo'}</text>
          <text x={mid(ESQ[1], ESQ[2])[0] + 48} y={mid(ESQ[1], ESQ[2])[1] + 6} fill="#1d4ed8">{ladosConMedida ? 'Ancho: 6 m' : 'Ancho'}</text>
          <text x={mid(ESQ[0], ESQ[2])[0]} y={mid(ESQ[0], ESQ[2])[1] + 6} fill="#374151" fontSize="14">Platea</text>
        </g>
      )}

      {/* medida */}
      {nMed >= 1 && largo > 0 && <line x1={pA[0]} y1={pA[1]} x2={pB[0]} y2={pB[1]} stroke="#16a34a" strokeWidth="5" strokeLinecap="round" style={sombra} />}
      {[MA, MB].slice(0, nMed).map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="9" fill="#fff" stroke="#16a34a" strokeWidth="3.5" style={sombra} />)}
      {cartel > 0 && (
        <g transform={`translate(${mAB[0]} ${mAB[1] - 34}) scale(${0.6 + 0.4 * cartel})`} opacity={cartel}>
          <rect x="-82" y="-24" width="164" height="44" rx="22" fill="#16a34a" style={sombra} />
          <text x="0" y="6" textAnchor="middle" fontSize="22" fontWeight="800" fill="#fff">12,4 m ± 0,3</text>
        </g>
      )}

      {/* toques */}
      {[...rippleDe(TAPS_ESQ).map((r, i) => [r, ESQ[i]]), ...rippleDe(TAPS_MED).map((r, i) => [r, [MA, MB][i]])].map(([r, p], i) => (r == null ? null : (
        <circle key={`r${i}`} cx={p[0]} cy={p[1]} r={10 + 26 * r} fill="none" stroke="#ffffff" strokeWidth={3 * (1 - r)} opacity={1 - r} />
      )))}

      {calibrada && t < 13.2 && (
        <g transform={`translate(${VW / 2} 40)`} opacity={entre(t, 10.2, 10.5)}>
          <rect x="-150" y="-22" width="300" height="44" rx="22" fill="#16a34a" style={sombra} />
          <text x="10" y="7" textAnchor="middle" fontSize="19" fontWeight="800" fill="#fff">✓ Cámara calibrada</text>
        </g>
      )}
    </svg>
  );
}

const Formulario = ({ t }) => {
  const vis = entre(t, 5.7, 6.1) * (1 - entre(t, 9.8, 10.1));
  if (vis <= 0) return null;
  const largo = t < 6.8 ? '' : t < 7.1 ? '1' : '10';
  const ancho = t < 8.2 ? '' : '6';
  const cursor = Math.floor(t * 2.5) % 2 === 0;
  const campo = (label, v, activo, color, lado) => (
    <div className="flex items-center gap-2">
      <span className="flex items-center gap-1 text-[11px] sm:text-sm font-semibold w-20 sm:w-28" style={{ color }}>
        <span className="inline-block w-3 h-1.5 rounded-full" style={{ background: color }} />{label}<span className="hidden sm:inline text-[10px] font-medium text-gray-500">({lado})</span>
      </span>
      <span className={`flex-1 rounded-lg border-2 px-2 py-0.5 sm:py-1 text-base sm:text-xl font-bold text-gray-900 bg-white ${activo ? 'border-blue-500' : 'border-gray-200'}`}>
        {v}{activo && cursor ? <span className="text-blue-500">|</span> : null}<span className="text-gray-400 text-sm font-medium ml-1">m</span>
      </span>
    </div>
  );
  return (
    <div className="absolute right-[10%] top-[4%] w-[46%] sm:w-[36%] rounded-xl bg-white/95 shadow-xl p-2 sm:p-3 space-y-1.5" style={{ opacity: vis, transform: `translateY(${(vis - 1) * 16}px)` }}>
      <div className="text-[10px] sm:text-xs font-semibold text-gray-700">¿Cuánto mide la platea que marcaste?</div>
      {campo('Largo', largo, t >= 6.3 && t < 7.6, '#dc2626', 'lado rojo')}
      {campo('Ancho', ancho, t >= 7.6 && t < 9.2, '#2563eb', 'lado azul')}
      <div className={`rounded-lg text-center text-xs sm:text-sm font-bold py-1 ${t >= 9.3 ? 'bg-blue-700 text-white scale-95' : 'bg-blue-600 text-white'} transition-transform`}>Listo</div>
    </div>
  );
};

const Herramientas = ({ t }) => {
  const vis = entre(t, 13.2, 13.6);
  if (vis <= 0) return null;
  return (
    <div className="absolute left-[3%] top-[4%] flex gap-1 sm:gap-1.5" style={{ opacity: vis }}>
      {[['Distancia', Ruler, true], ['Superficie', Square, false], ['Altura', MoveVertical, false]].map(([n, I, act]) => (
        <span key={n} className={`flex flex-col items-center rounded-lg px-1.5 sm:px-2.5 py-1 text-[9px] sm:text-xs font-semibold shadow ${act ? 'bg-blue-600 text-white' : 'bg-white/95 text-gray-700'}`}>
          <I className="w-3.5 h-3.5 sm:w-5 sm:h-5" />{n}
        </span>
      ))}
    </div>
  );
};

/** congelar: segundo fijo (sólo para capturas en local) */
const MedirTutorial = ({ onCerrar, onEmpezar, congelar = null }) => {
  const [t, setT] = useState(congelar ?? 0);
  const [corriendo, setCorriendo] = useState(congelar == null);
  const inicio = useRef(null);
  const saltear = useRef(null);

  useEffect(() => {
    if (!corriendo) return undefined;
    let raf;
    inicio.current = performance.now() - t * 1000;
    const paso = (ahora) => {
      const s = (ahora - inicio.current) / 1000;
      if (s >= DURACION) { setT(DURACION); setCorriendo(false); return; }
      setT(s);
      raf = requestAnimationFrame(paso);
    };
    raf = requestAnimationFrame(paso);
    return () => cancelAnimationFrame(raf);
  }, [corriendo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    saltear.current?.focus();
    const tecla = (e) => { if (e.key === 'Escape') onCerrar(); };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [onCerrar]);

  const deNuevo = useCallback(() => { setT(0); setCorriendo(true); }, []);
  const fin = t >= DURACION;
  const etapa = ETAPAS.findIndex((e) => t < e.hasta);
  const texto = fin ? '¡Así de fácil!' : ETAPAS[Math.max(0, etapa)].texto;
  const [hx, hy] = posMano(t);
  const tocando = [...TAPS_ESQ, ...TAPS_MED].some((x) => t >= x - 0.12 && t < x + 0.12);
  const manoVisible = !fin && ((t > 0.4 && t < 6.1) || (t > 13.4 && t < 17.3));

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-gray-900/70 p-3 sm:p-6" role="dialog" aria-modal="true" aria-label="Cómo medir sobre la foto">
      <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="relative w-full bg-gray-100" style={{ aspectRatio: `${VW} / ${VH}` }}>
          <Escena t={t} />
          <Formulario t={t} />
          <Herramientas t={t} />
          {manoVisible && (
            <div className="pointer-events-none absolute" style={{ left: `${(hx / VW) * 100}%`, top: `${(hy / VH) * 100}%`, transform: `translate(-34%, -6%) scale(${tocando ? 0.86 : 1})`, transition: 'transform 80ms' }}>
              <Pointer className="w-9 h-9 sm:w-12 sm:h-12 text-gray-900" fill="#ffffff" strokeWidth={1.6} style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,.4))' }} />
            </div>
          )}
          {fin && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white/80 backdrop-blur-[2px]">
              <div className="w-14 h-14 rounded-full bg-green-600 text-white flex items-center justify-center"><Check className="w-8 h-8" /></div>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">¡Así de fácil!</p>
              <p className="text-sm text-gray-600 px-6 text-center">Calibrás una vez y después medís en cualquier foto.</p>
            </div>
          )}
          <button type="button" onClick={onCerrar} aria-label="Cerrar" className="absolute top-2 right-2 w-9 h-9 rounded-full bg-white/90 text-gray-700 flex items-center justify-center shadow hover:bg-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="h-1 bg-gray-100"><div className="h-1 bg-blue-600" style={{ width: `${(Math.min(t, DURACION) / DURACION) * 100}%` }} /></div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4">
          <p className="flex-1 text-lg sm:text-xl font-bold text-gray-900 min-h-[1.75rem]" aria-live="polite">{texto}</p>
          <div className="flex gap-2">
            {fin ? (
              <>
                <button type="button" onClick={deNuevo} className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"><RotateCcw className="w-4 h-4" /> Ver de nuevo</button>
                <button type="button" onClick={onEmpezar || onCerrar} className="inline-flex items-center gap-1.5 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"><Play className="w-4 h-4" /> {onEmpezar ? 'Empezar' : 'Entendido'}</button>
              </>
            ) : (
              <button ref={saltear} type="button" onClick={onCerrar} className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50">Saltear</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MedirTutorial;
export const TUTORIAL_DURACION = DURACION;
